import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { LoginRequest, LoginResponse, ApiResponse, TemplatesResponse, UploadFormData, TemplateUsersResponse, AddUsersWithRolesRequest, RemoveUsersRequest, TemplateSharingResponse, UpdateUserRoleRequest, UserRole, User } from '../types';

class ApiService {
    private baseURL: string;
    private token: string | null = null;

    constructor(baseURL: string) {
        this.baseURL = baseURL;
    }

    setToken(token: string | null) {
        this.token = token;
    }

    private getAuthHeaders(): Record<string, string> {
        return this.token ? { Authorization: `Bearer ${this.token}` } : {};
    }

    private async request<T>(config: AxiosRequestConfig): Promise<T> {
        const response = await axios({
            ...config,
            baseURL: this.baseURL,
            headers: {
                'Content-Type': 'application/json',
                ...this.getAuthHeaders(),
                ...config.headers,
            },
        });
        return response.data;
    }

    // Auth endpoints
    async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
        return this.request<ApiResponse<LoginResponse>>({
            method: 'POST',
            url: '/auth/login',
            data: credentials,
        });
    }

    // Template endpoints
    async getTemplates(): Promise<ApiResponse<TemplatesResponse>> {
        return this.request<ApiResponse<TemplatesResponse>>({
            method: 'GET',
            url: '/templates',
        });
    }

    async createTemplate(formData: UploadFormData): Promise<ApiResponse<any>> {
        return this.request<ApiResponse<any>>({
            method: 'POST',
            url: '/templates',
            data: formData,
        });
    }

    async updateTemplate(id: number, formData: UploadFormData): Promise<ApiResponse<any>> {
        return this.request<ApiResponse<any>>({
            method: 'PUT',
            url: `/templates/${id}`,
            data: formData,
        });
    }

    async deleteTemplate(id: number): Promise<ApiResponse<any>> {
        return this.request<ApiResponse<any>>({
            method: 'DELETE',
            url: `/templates/${id}`,
        });
    }

    // Template sharing endpoints
    async getTemplateUsers(templateId: number): Promise<ApiResponse<TemplateUsersResponse>> {
        return this.request<ApiResponse<TemplateUsersResponse>>({
            method: 'GET',
            url: `/templates/${templateId}/users`,
        });
    }

    async addUsersToTemplate(templateId: number, users: { emailId: string; role: UserRole }[]): Promise<ApiResponse<TemplateSharingResponse>> {
        return this.request<ApiResponse<TemplateSharingResponse>>({
            method: 'POST',
            url: `/templates/${templateId}/users/with-roles`,
            data: { users },
        });
    }

    async removeUsersFromTemplate(templateId: number, emailIds: string[]): Promise<ApiResponse<TemplateSharingResponse>> {
        return this.request<ApiResponse<TemplateSharingResponse>>({
            method: 'DELETE',
            url: `/templates/${templateId}/users`,
            data: { emailIds },
        });
    }

    // User role management endpoints
    async updateUserRole(templateId: number, emailId: string, role: UserRole): Promise<ApiResponse<any>> {
        return this.request<ApiResponse<any>>({
            method: 'PUT',
            url: `/templates/${templateId}/users/role`,
            data: { emailId, role },
        });
    }

    // User management endpoints (Admin only)
    async getAllUsers(): Promise<ApiResponse<{ users: User[]; total: number }>> {
        return this.request<ApiResponse<{ users: User[]; total: number }>>({
            method: 'GET',
            url: '/users',
        });
    }

    async createUser(userData: { name: string; email: string; password: string }): Promise<ApiResponse<User>> {
        return this.request<ApiResponse<User>>({
            method: 'POST',
            url: '/auth/register',
            data: userData,
        });
    }

    async deleteUser(userId: number): Promise<ApiResponse<{ message: string }>> {
        return this.request<ApiResponse<{ message: string }>>({
            method: 'DELETE',
            url: `/users/${userId}`,
        });
    }

    async resetUserPassword(userId: number, newPassword: string): Promise<ApiResponse<{ message: string }>> {
        return this.request<ApiResponse<{ message: string }>>({
            method: 'POST',
            url: '/users/reset-password',
            data: { userId, newPassword },
        });
    }

    // Public template endpoint (no authentication required)
    async getPublicTemplate(encryptedId: string): Promise<ApiResponse<any>> {
        return this.request<ApiResponse<any>>({
            method: 'GET',
            url: `/public/template/${encryptedId}`,
        });
    }
}

// Create a singleton instance
const apiService = new ApiService('https://hsa-html-viewer.authentific.workers.dev/api');

export default apiService; 